import Letterhead from './Letterhead';
import { fmtDMY } from '../lib/format';

type Row = Record<string, unknown>;
export interface DailyLogData {
  equipment: Row | null;
  template: { sections: Array<{ key: string; label: string; fields: Array<{ key: string; label: string; unit: string }> }> };
  date: string;
  org?: { name: string; subtitle: string; letterheadUrl: string };
  rows: Row[];
}

const COL: Record<string, string> = {
  ht_voltage: 'HT Voltage', ht_amp: 'HT Amp', ht_freq: 'HT Frequency', ht_amp_tr1: 'HT Amp TR-1', ht_amp_tr2: 'HT Amp TR-2',
  tr1_voltage: 'TR1 Voltage', tr1_amp: 'TR1 Amp', tr1_freq: 'TR1 Frequency', tr1_temp_r: 'TR1 Temp-R', tr1_temp_y: 'TR1 Temp-Y', tr1_temp_b: 'TR1 Temp-B', tr1_pf: 'TR1 PF',
  tr2_voltage: 'TR2 Voltage', tr2_amp: 'TR2 Amp', tr2_freq: 'TR2 Frequency', tr2_temp_r: 'TR2 Temp-R', tr2_temp_y: 'TR2 Temp-Y', tr2_temp_b: 'TR2 Temp-B', tr2_pf: 'TR2 PF',
};
export function cell(r: Row, f: { key: string; label: string }): string {
  for (const g of [COL[f.key], f.label]) {
    if (g && r[g] !== undefined && r[g] !== '') return String(r[g]);
  }
  return '';
}

/** The A4 daily-logbook document (letterhead + grouped grid + sign block). Read-only. */
export default function DailyLogDoc({ data, equipmentId }: { data: DailyLogData; equipmentId: string }) {
  const sections = data.template?.sections ?? [];
  const fields = sections.flatMap((s) => s.fields ?? []);
  const eq = data.equipment as { name?: string; location?: string } | null;
  const equipName = eq?.name ?? equipmentId;
  const equipLoc = eq?.location ?? '';
  const title = (data.template as { title?: string })?.title ?? 'Reading Log';
  const rows = data.rows ?? [];

  return (
    <div className="print-area a4">
      <Letterhead org={data.org} />
      <div className="doc-head">
        <h2 className="doc-title">Daily Logbook — {title}</h2>
        <div className="doc-sub">{equipName}{equipLoc ? ` · ${equipLoc}` : ''}</div>
        <div className="doc-meta">
          <span>Date: <strong>{fmtDMY(data.date)}</strong></span>
          <span>Equipment ID: <strong>{equipmentId}</strong></span>
          <span>Readings: <strong>{rows.length}</strong></span>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="muted">No readings recorded on this date.</p>
      ) : (
        <div className="tableScroll">
          <table className="grid paper">
            <thead>
              <tr>
                <th rowSpan={2}>Time</th>
                {sections.map((s) => <th key={s.key} colSpan={s.fields.length} className="sec">{s.label}</th>)}
                <th rowSpan={2}>Operator</th>
                <th rowSpan={2}>Remarks</th>
              </tr>
              <tr>
                {fields.map((f) => <th key={f.key}>{f.label}{f.unit ? <><br /><span className="u">{f.unit}</span></> : null}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{String(r['Time'] ?? '')}</td>
                  {fields.map((f) => <td key={f.key}>{cell(r, f)}</td>)}
                  <td>{String(r['Operator Name'] ?? '')}</td>
                  <td className="rmk">{String(r['Observation / Remarks'] ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="signrow"><div className="sign-ee">Electrical Engineer</div></div>
    </div>
  );
}
